import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useClonePublishedProject } from './hooks'
import { ApiRequestError } from './client'
import { useAuth } from '../contexts/AuthContext'
import { createNadDebug } from '../utils/debugLog'

const nad = createNadDebug('dw:templates-gallery')

/**
 * Shared "Use this template" / published-clone flow (RFC-0934 P1).
 *
 * ONE clone implementation behind BOTH PublishedProjectPage and the templates
 * gallery — no duplicated wiring:
 * - Reuses {@link useClonePublishedProject} (no new mutation) → clones the frozen
 *   published snapshot and navigates IN-PLACE (P0-6: a post-await `window.open`
 *   is popup-blocked).
 * - Maps the SPECIFIC clone failure (P0-7) to a user-facing message.
 * - Logged-out → routes to `/login` (no auto-resume). The caller renders the
 *   "Sign in to use" label off `isAuthenticated`.
 * - Fail-loud: never fires a clone with a null share_token/version_slug
 *   (partially-provisioned template) — that request is guaranteed to 404.
 */
export function useCloneTemplate() {
  const { t } = useTranslation('pages')
  const { user, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const cloneMutation = useClonePublishedProject()

  const isAuthenticated = !!user

  const cloneTemplate = useCallback(
    async (shareToken?: string | null, versionSlug?: string | null) => {
      // Auth is not known until AuthProvider's getSession() resolves — never
      // route to /login mid-hydration (an already-signed-in user on a hard
      // refresh would otherwise be bounced despite a valid session).
      if (authLoading) return
      if (!isAuthenticated) {
        nad({ event: 'clone-signin-redirect' })
        navigate('/login')
        return
      }
      if (!shareToken || !versionSlug) {
        nad({ event: 'clone-skipped-null-target', hasToken: !!shareToken, hasSlug: !!versionSlug })
        return
      }
      nad({ event: 'clone-start', shareToken })
      try {
        const result = await cloneMutation.mutateAsync({ projectToken: shareToken, versionSlug })
        nad({ event: 'clone-success', newProjectId: result.id })
        navigate(`/project/${result.id}/workspace`)
      } catch (err) {
        const status = err instanceof ApiRequestError ? err.status : 'network'
        nad({ event: 'clone-error', status })
        // Error surfaced via `cloneErrorMessage`.
      }
    },
    [authLoading, isAuthenticated, navigate, cloneMutation],
  )

  const cloneErrorMessage = useMemo(() => {
    if (!cloneMutation.isError) return null
    const status =
      cloneMutation.error instanceof ApiRequestError ? cloneMutation.error.status : undefined
    if (status === 404) return t('publishedProject.cloneFailedNotFound')
    if (status === 409) return t('publishedProject.cloneFailedUnavailable')
    return t('publishedProject.cloneFailed')
  }, [cloneMutation.isError, cloneMutation.error, t])

  return {
    isAuthenticated,
    authLoading,
    cloneTemplate,
    isCloning: cloneMutation.isPending,
    cloneErrorMessage,
  }
}
